/**
 * CreateAccountForExistingModal — F12.M Etap D Part 2 (extracted z Employees.tsx).
 *
 * Modal „Załóż konto użytkownika" dla istniejącego pracownika.
 * Endpoint: POST /api/employees/:id/create-account.
 *
 * Backend: tworzy user'a w tej samej transakcji co invite, sklejka z employee.user_id.
 * Email idzie do invite flow (user setuje hasło ze swojej strony przez magic link).
 *
 * Role (wiele, w kolejności — ADR-0103) wybiera się spośród ról FIRMY PRACOWNIKA; konto dostaje
 * dostęp do tej firmy z tymi rolami. Na starcie podpowiadamy rolę domyślną dla członka firmy —
 * bez żadnej roli konto nie dostaje dostępu do firmy (nic by nie widziało).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RECORD_LANGUAGES, type RecordLanguage } from '@saas/shared';
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Dropdown } from '@/components/Dropdown';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { CompanyRolesEditor } from '@/components/users/CompanyRolesEditor';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { recordLanguageLabel } from '@/lib/core-field-option-labels';
import { useRoleTemplates } from '@/lib/use-permissions';

interface Props {
  employeeId: string;
  firstName: string;
  lastName: string;
  /** Firma pracownika — konto dostaje w niej dostęp z wybranymi rolami. */
  companyId: string;
  /** Default email zaproponowany w polu (np. workEmail pracownika). */
  defaultEmail?: string | null;
  onClose: () => void;
  onCreated: () => void;
}

export function CreateAccountForExistingModal({
  employeeId,
  firstName,
  lastName,
  companyId,
  defaultEmail,
  onClose,
  onCreated,
}: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const [email, setEmail] = useState(defaultEmail ?? '');
  const [displayName, setDisplayName] = useState(`${firstName} ${lastName}`);
  const [language, setLanguage] = useState<RecordLanguage>('pl');

  // Role są per firma — do wyboru wyłącznie role firmy pracownika.
  const rolesQ = useRoleTemplates();
  const companyRoles = useMemo(
    () => (rolesQ.data?.roleTemplates ?? []).filter((r) => r.companyId === companyId),
    [rolesQ.data, companyId],
  );
  // null = użytkownik jeszcze nie ruszał ról → podpowiedź (rola domyślna członka firmy).
  const [pickedRoleIds, setPickedRoleIds] = useState<string[] | null>(null);
  const defaultRoleId = companyRoles.find((r) => r.isDefaultMember)?.id;
  const roleIds = pickedRoleIds ?? (defaultRoleId ? [defaultRoleId] : []);

  const submitMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(`/employees/${employeeId}/create-account`, {
        ...(email ? { email } : {}),
        ...(displayName ? { displayName } : {}),
        language,
        roleTemplateIds: roleIds,
      });
      return res.data;
    },
    onSuccess: () => onCreated(),
    onError: (err) => {
      if (isAxiosError(err)) {
        const code = err.response?.data?.error as string | undefined;
        if (code) {
          const msg = t(`tenant.employees.toast.errors.${code}`, { defaultValue: '' });
          if (msg) {
            toast.show({ variant: 'error', title: msg });
            return;
          }
        }
      }
      toast.show({ variant: 'error', title: t('tenant.employees.toast.createAccountError') });
    },
  });

  const canSubmit = !!(email || defaultEmail);

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={t('tenant.employees.createAccountModal.title')}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => submitMutation.mutate()}
            disabled={!canSubmit || submitMutation.isPending}
            data-testid="employee-create-account-submit"
          >
            {t('common.create')}
          </Button>
        </div>
      }
    >
      <p className="mb-3 text-sm">
        {t('tenant.employees.createAccountModal.body', {
          name: `${firstName} ${lastName}`,
        })}
      </p>
      <div className="space-y-3">
        <Input
          label={t('tenant.employees.createModal.account.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={defaultEmail ?? ''}
          data-testid="employee-create-account-existing-email"
        />
        <Input
          label={t('tenant.employees.createModal.account.displayName')}
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <FormField
          label={t('tenant.employees.createAccountModal.roles')}
          hint={t('tenant.employees.createAccountModal.rolesHint')}
        >
          <CompanyRolesEditor
            companyId={companyId}
            testIdKey="employee-create-account"
            assigned={roleIds.map((id) => ({
              id,
              name: companyRoles.find((r) => r.id === id)?.name ?? '',
            }))}
            available={companyRoles}
            onChange={setPickedRoleIds}
            busy={rolesQ.isLoading}
          />
        </FormField>
        <FormField label={t('tenant.employees.createModal.account.language')}>
          <Dropdown
            value={language}
            onChange={(v) => setLanguage(v as RecordLanguage)}
            options={RECORD_LANGUAGES.map((code) => ({
              value: code,
              label: recordLanguageLabel(code, t),
            }))}
          />
        </FormField>
      </div>
    </Modal>
  );
}
